import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { extractErrorMessage } from 'helpers/extractErrorMessage';
import type { FolderPath } from 'helpers/folderPaths';

export interface PythonLibrary {
  python_library_id: number;
  name: string;
  filename: string;
  description?: string;
  content?: string;
  hash: string;
  // Folder support (nap-backend #1076). Not zone-scoped.
  folder_id?: number | null;
  folder_path?: FolderPath | null;
  created_at?: string;
  updated_at?: string;
}

interface PythonLibrariesState {
  items: PythonLibrary[];
  status: string | null;
  error: string | null;
  loading: {
    fetchPythonLibraries: boolean;
    createPythonLibrary: boolean;
    updatePythonLibrary: boolean;
    deletePythonLibrary: boolean;
  };
}

const initialState: PythonLibrariesState = {
  items: [],
  status: null,
  error: null,
  loading: {
    fetchPythonLibraries: false,
    createPythonLibrary: false,
    updatePythonLibrary: false,
    deletePythonLibrary: false,
  },
};

export const fetchPythonLibraries = createAsyncThunk('pythonLibraries/fetchPythonLibraries', async () => {
  const res = await api.get('/python_libraries');
  return res.data;
});

export const fetchPythonLibraryById = createAsyncThunk('pythonLibraries/fetchPythonLibraryById', async (id: number) => {
  const res = await api.get(`/python_libraries/${id}`);
  return res.data;
});

export const createPythonLibrary = createAsyncThunk<
  PythonLibrary,
  {
    name: string;
    filename: string;
    description?: string;
    content: string;
    folder_id: number | null;
  },
  { rejectValue: string }
>('pythonLibraries/createPythonLibrary', async (body, { rejectWithValue }) => {
  try {
    const res = await api.post('/python_libraries', body);
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.error || err?.message || 'Failed to create library');
  }
});

export const updatePythonLibrary = createAsyncThunk<
  PythonLibrary,
  {
    python_library_id: number;
    name: string;
    filename: string;
    description?: string;
    content: string;
    folder_id: number | null;
  },
  { rejectValue: string }
>('pythonLibraries/updatePythonLibrary', async ({ python_library_id, ...body }, { rejectWithValue }) => {
  try {
    const res = await api.put(`/python_libraries/${python_library_id}`, body);
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.error || err?.message || 'Failed to update library');
  }
});

export const deletePythonLibrary = createAsyncThunk<number, number, { rejectValue: string }>(
  'pythonLibraries/deletePythonLibrary',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/python_libraries/${id}`);
      return id;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.error || err?.message || 'Failed to delete library');
    }
  }
);

const pythonLibrariesSlice = createSlice({
  name: 'pythonLibraries',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPythonLibraries.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.fetchPythonLibraries = true;
      })
      .addCase(fetchPythonLibraries.fulfilled, (state, action) => {
        state.items = action.payload;
        state.status = 'succeeded';
        state.loading.fetchPythonLibraries = false;
      })
      .addCase(fetchPythonLibraries.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch python libraries';
        state.loading.fetchPythonLibraries = false;
      })

      .addCase(fetchPythonLibraryById.fulfilled, (state, action) => {
        const idx = state.items.findIndex((lib) => lib.python_library_id === action.payload.python_library_id);
        if (idx !== -1) {
          state.items[idx] = action.payload;
        } else {
          state.items.push(action.payload);
        }
      })

      .addCase(createPythonLibrary.pending, (state) => {
        state.loading.createPythonLibrary = true;
        state.error = null;
      })
      .addCase(createPythonLibrary.fulfilled, (state, action) => {
        state.items.push(action.payload);
        state.loading.createPythonLibrary = false;
        const toastId = 'data-created-python-library';
        if (!toast.isActive(toastId)) {
          toast.success('Python library created successfully!', { toastId });
        }
      })
      .addCase(createPythonLibrary.rejected, (state, action) => {
        state.loading.createPythonLibrary = false;
        state.error = extractErrorMessage(action.payload, 'Failed to create library');
        const toastId = 'data-failed-to-create-python-library';
        if (!toast.isActive(toastId)) {
          toast.error(`Failed to create library! ${state.error}`, {
            toastId,
          });
        }
      })

      .addCase(updatePythonLibrary.pending, (state) => {
        state.loading.updatePythonLibrary = true;
        state.error = null;
      })
      .addCase(updatePythonLibrary.fulfilled, (state, action) => {
        const idx = state.items.findIndex((lib) => lib.python_library_id === action.payload.python_library_id);
        if (idx !== -1) state.items[idx] = action.payload;
        state.loading.updatePythonLibrary = false;
        const toastId = 'data-updated-python-library';
        if (!toast.isActive(toastId)) {
          toast.success('Python library updated successfully!', { toastId });
        }
      })
      .addCase(updatePythonLibrary.rejected, (state, action) => {
        state.loading.updatePythonLibrary = false;
        state.error = extractErrorMessage(action.payload, 'Failed to update library');
        const toastId = 'data-failed-to-update-python-library';
        if (!toast.isActive(toastId)) {
          toast.error(`Failed to update library! ${state.error}`, {
            toastId,
          });
        }
      })

      .addCase(deletePythonLibrary.pending, (state) => {
        state.loading.deletePythonLibrary = true;
        state.error = null;
      })
      .addCase(deletePythonLibrary.fulfilled, (state, action) => {
        state.items = state.items.filter((lib) => lib.python_library_id !== action.payload);
        state.loading.deletePythonLibrary = false;
        const toastId = 'data-deleted-python-library';
        if (!toast.isActive(toastId)) {
          toast.success('Python library deleted successfully!', { toastId });
        }
      })
      .addCase(deletePythonLibrary.rejected, (state, action) => {
        state.loading.deletePythonLibrary = false;
        state.error = extractErrorMessage(action.payload, 'Failed to delete library');
        const toastId = 'data-failed-to-delete-python-library';
        if (!toast.isActive(toastId)) {
          toast.error(`Failed to delete library! ${state.error}`, {
            toastId,
          });
        }
      });
  },
});

export default pythonLibrariesSlice.reducer;
